<script setup lang="ts">
import { watch } from 'vue'

import {
  chartColor,
  chartTime,
  dateAxis,
  formatChartDate,
  formatDecimal,
  useChartJs
} from '@/shared/lib'

import type { AreaChartProps } from './types'

const props = withDefaults(defineProps<AreaChartProps>(), {
  axisTitle: '',
  color: '--el-color-primary',
  signed: false,
  valueSuffix: '',
  height: 500
})

function tooltipValue(value: number): string {
  const sign = props.signed && value > 0 ? '+' : ''

  return `${sign}${formatDecimal(value)}${props.valueSuffix}`
}

const { element, render } = useChartJs({
  hasData: () => props.data.length > 0,
  draw: ({ Chart }, host) => {
    const color = chartColor(props.color)
    const negative = chartColor('--el-color-danger')
    const zeroLine = chartColor('--el-text-color-regular')
    const gridLine = chartColor('--el-border-color-lighter')
    const times = props.data.map((point) => chartTime(point.date))
    const below = (value: number | null | undefined) =>
      props.signed && value != null && value < 0

    return new Chart(host, {
      type: 'line',
      data: {
        datasets: [
          {
            label: props.title,
            data: props.data.map((point, index) => ({
              x: times[index] ?? 0,
              y: point.value
            })),
            borderColor: color,
            borderWidth: 1,
            // `#rrggbb` plus an alpha byte: the area under the line at 15%.
            fill: {
              target: 'origin',
              above: `${color}26`,
              below: `${negative}26`
            },
            // A segment is one colour end to end, so one that crosses zero takes the side
            // it spends longer on.
            segment: {
              borderColor: (context) =>
                below(
                  ((context.p0.parsed.y ?? 0) + (context.p1.parsed.y ?? 0)) / 2
                )
                  ? negative
                  : color
            },
            pointRadius: props.data.length === 1 ? 3 : 0,
            pointBackgroundColor: (context) =>
              below(context.parsed?.y) ? negative : color,
            pointBorderColor: (context) =>
              below(context.parsed?.y) ? negative : color,
            // amCharts' `tensionX: 0.77`.
            tension: 0.23
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { right: 12 } },
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            displayColors: false,
            backgroundColor: (context) =>
              below(context.tooltip.dataPoints?.[0]?.parsed.y)
                ? negative
                : color,
            callbacks: {
              title: (items) => formatChartDate(items[0]?.parsed.x ?? 0),
              label: (item) =>
                `${props.title}: ${tooltipValue(item.parsed.y ?? 0)}`
            }
          }
        },
        scales: {
          x: dateAxis(times),
          y: {
            min: props.signed ? undefined : 0,
            // `suggested`, not `min`/`max`: zero joins the range and the data keeps the rest.
            suggestedMin: props.signed ? 0 : undefined,
            suggestedMax: props.signed ? 0 : undefined,
            grid: props.signed
              ? {
                  color: (context) =>
                    context.tick.value === 0 ? zeroLine : gridLine,
                  lineWidth: (context) => (context.tick.value === 0 ? 2 : 1)
                }
              : undefined,
            title: { display: Boolean(props.axisTitle), text: props.axisTitle }
          }
        }
      }
    })
  }
})

watch(() => props.data, render, { flush: 'post' })
</script>

<template>
  <div class="area-chart" :style="{ height: `${height}px` }">
    <canvas ref="element" />
  </div>
</template>

<style lang="scss" scoped>
.area-chart {
  position: relative;
}
</style>
